import {
  moderationApi,
  type BulkDeleteEntitiesParams,
  type BulkDeleteEntitiesResponse,
  type BulkDeleteEntityType,
  type BulkSubmitForReviewParams,
  type BulkSubmitForReviewResponse,
  type ChangeRequestEntityType,
} from '@noor/eats-api';
import { toChangeRequest } from '../lib/map-change-request';
import type { ChangeRequestPage, ChangeRequestStatusFilter } from '../model/schema';

/** Server-side narrowing of the queue, straight from the URL. */
export interface ChangeRequestFilters {
  /** The active status tab; absent means every state. */
  status?: ChangeRequestStatusFilter | undefined;
}

/** One page of the queue. */
export interface ChangeRequestPageParams {
  limit: number;
  offset: number;
}

export const changeRequestApi = {
  /**
   * The merchant's own queue — `GET /merchant/change-requests`.
   *
   * The status filter is sent as a query param and NOT re-applied client-side,
   * unlike the menu catalog's filters: this endpoint documents `status` as a
   * real filter, and re-filtering a PAGED read would silently shrink pages
   * (page 2 of "pending" would drop rows the server already narrowed).
   */
  async list(
    filters: ChangeRequestFilters,
    page: ChangeRequestPageParams,
  ): Promise<ChangeRequestPage> {
    const { list, meta } = await moderationApi.list({
      ...(filters.status === undefined ? {} : { status: filters.status }),
      limit: page.limit,
      offset: page.offset,
    });
    return { items: list.map(toChangeRequest), total: meta.total };
  },

  /** Hand an entity to moderation. Returns the request's id for a later cancel. */
  async submitForReview(
    entityType: ChangeRequestEntityType,
    entityId: string,
  ): Promise<string | undefined> {
    const request = await moderationApi.submitForReview(entityType, entityId);
    return request?.id;
  },

  /**
   * Submit drafts of an entity type to moderation —
   * `POST /merchant/entities/{entity_type}/submit-for-review`.
   * Pass `entity_ids` to submit only the selected entities; omit it to
   * submit every eligible draft.
   */
  async submitAllForReview(
    entityType: ChangeRequestEntityType,
    params?: BulkSubmitForReviewParams,
  ): Promise<BulkSubmitForReviewResponse> {
    return moderationApi.submitAllForReview(entityType, params);
  },

  /** Withdraw a pending request, addressed by the REQUEST's id. */
  async cancel(changeRequestId: string): Promise<void> {
    await moderationApi.cancel(changeRequestId);
  },

  /**
   * Bulk delete entities of an entity type —
   * `POST /api/v1/merchant/entities/{entity_type}/bulk-delete`.
   */
  async bulkDelete(
    entityType: BulkDeleteEntityType,
    params: BulkDeleteEntitiesParams,
  ): Promise<BulkDeleteEntitiesResponse> {
    return moderationApi.bulkDelete(entityType, params);
  },
};
